import { useContext } from "react";

import { useTranslation } from "react-i18next";

import type { JointOrAilment } from "api/schemas/Patients";
import type { LateralLocation, Location } from "api/schemas/shared/Treatment";
import { useFeatureToggleContext } from "contexts/FeatureToggleContext";
import { PatientsFilterContext } from "utils/contexts";
import { Feature } from "utils/features/types";
import { translateTreatment } from "utils/translations";

import { TherapistAssignmentRole } from "../queries/TherapistAssignmentRole";

import { StyledCheckBox } from "./helpers";
import { getTreatmentId } from "./stringifyJoint";
import type { Filter, Filters } from "./useFilters";

export const useFilterOptions = (uniqueTreatments: JointOrAilment[]) => {
  const { t } = useTranslation();
  const { activeFilters, toggleFilter, setFilter, getFilterState } = useContext<Filters>(PatientsFilterContext);
  const { hasFeature } = useFeatureToggleContext();

  const createOptions = (filters: Filter[], prefix = "patients") =>
    filters.map(f => ({ label: t(`${prefix}.${f}` as never), value: f }));

  const getSelected = (filterArray: (Filter | TherapistAssignmentRole)[]) =>
    activeFilters.filter(f => filterArray.includes(f as Filter));

  const handleMultiSelectChange = (
    filterArray: (Filter | TherapistAssignmentRole)[],
    event: { target: { value: unknown } }
  ) => {
    const selected = event.target.value as (Filter | TherapistAssignmentRole)[];
    filterArray.forEach(f => {
      const shouldToggle = activeFilters.includes(f as Filter) !== selected.includes(f);
      if (shouldToggle) toggleFilter(f as Filter);
    });
  };

  const statuses: Filter[] = ["active", "inactive", "invited"];
  const statusCheckboxes = statuses.map(status => (
    <StyledCheckBox
      key={`filter-${status}`}
      name={`filter-${status}`}
      label={t(`patients.statuses.${status}` as never)}
      checked={activeFilters.includes(status)}
      onChange={() => toggleFilter(status)}
    />
  ));

  const tasks: Filter[] = ["new_patients", "new_comments", "new_messages", "appointment_to_schedule"];
  if (hasFeature(Feature.SHOW_AWAITING_TREATMENT_READY)) tasks.push("awaiting_treatment_ready");
  const taskOptions = createOptions(tasks);

  const categories: Filter[] = ["prio", "hybrid_care"];
  const membershipStatuses: Filter[] = ["selfcare", "guided_care"];
  const assignmentRoles: TherapistAssignmentRole[] = [
    TherapistAssignmentRole.MainTherapist,
    TherapistAssignmentRole.SubstituteTherapist,
  ];
  const assignmentRolesPhrases: Record<TherapistAssignmentRole, string> = {
    [TherapistAssignmentRole.MainTherapist]: t(`patients.main_therapist`),
    [TherapistAssignmentRole.SubstituteTherapist]: t(`patients.substitute_therapist`),
  };

  const ailmentOptions = uniqueTreatments.map(treatment => ({
    value: getTreatmentId(treatment),
    label: translateTreatment({
      ailment: treatment.ailment,
      lateral_location: treatment.joint.lateral_location as LateralLocation,
      location: treatment.joint.location as Location,
    }),
  }));

  const primaryJointState = getFilterState("primary_joint");

  return {
    createOptions,
    getSelected,
    handleMultiSelectChange,

    statusCheckboxes,

    tasks,
    taskOptions,

    categories,

    membershipStatuses,

    assignmentRoles,
    assignmentRolesPhrases,

    ailmentOptions,
    primaryJointState,

    setFilter,
  };
};
